import { useCurrentClient } from '@pbk-web/state';
import { getEmailDomain } from '@pbk-web/utils';
import type { PostHog } from 'posthog-js';
import { useFeatureFlagEnabled, usePostHog } from 'posthog-js/react';
import { useEffect, useSyncExternalStore } from 'react';

import { useCurrentUser } from '../global-state/Auth';

const IS_LOCALHOST = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

/**
 * On localhost with no PostHog key configured, flags default on so dev works with zero setup.
 * Once a key is present (even locally), the real flag + its evaluation context are respected — so
 * environment gating can be tested locally by setting `VITE_PUBLIC_POSTHOG_KEY` + `VITE_ENVIRONMENT`.
 */
const FORCE_ON = IS_LOCALHOST && !import.meta.env.VITE_PUBLIC_POSTHOG_KEY;

type OrgKey = string | null;

// "No org confirmed loaded yet" — distinct from `null` (explicitly org-less). Returned value stays
// withheld until the coordinator settles on the org the current flags were actually evaluated against.
const UNKNOWN = Symbol('posthog-flags-unsettled');

/**
 * Module-level flag-reload coordinator.
 *
 * PostHog reloads flags on the one shared client, and `onFeatureFlags` doesn't say which org a given
 * reload evaluated — it also fires once with cached flags on subscribe and again with `errorsLoading`
 * on failure. So correlation can't live per hook or be read from the live org at callback time. Here we
 * record the org each reload was *requested* for and settle onto it only when a successful reload lands,
 * ignoring the cached fire (nothing requested) and failures (previous flags retained). State resets when
 * a new client instance binds (fresh flag state).
 */
let boundClient: PostHog | undefined;
let lastRequestedKey: string | undefined; // domain+org we last pushed; dedups across consumers
let requestedOrg: OrgKey | typeof UNKNOWN = UNKNOWN; // org of the most recent reload request
let reloadPending = false; // a requested reload is awaiting its callback
let settledOrg: OrgKey | typeof UNKNOWN = UNKNOWN; // org the last successful reload evaluated
const settledListeners = new Set<() => void>();

const bindCoordinator = (posthog: PostHog) => {
  if (boundClient === posthog) return;
  boundClient = posthog;
  lastRequestedKey = undefined;
  requestedOrg = UNKNOWN;
  reloadPending = false;
  settledOrg = UNKNOWN;

  posthog.onFeatureFlags((_flags, _variants, context) => {
    // Failed reload keeps the prior flags — it proves nothing about the requested org.
    if (context?.errorsLoading) return;
    // A fire with nothing pending is the initial/cached load, not a reload we asked for.
    if (!reloadPending) return;
    reloadPending = false;
    settledOrg = requestedOrg;
    settledListeners.forEach((fn) => fn());
  });
};

const requestFlagsForOrg = (posthog: PostHog, domain: string, org: OrgKey) => {
  const key = `${domain}|${org ?? ''}`;
  if (key === lastRequestedKey) return; // another consumer already requested this exact combo
  lastRequestedKey = key;
  requestedOrg = org;
  reloadPending = true;
  // `null`, not `undefined`: PostHog merges person properties, so an absent org must be sent
  // explicitly to clear a previously-set one rather than leaving the prior org in place.
  posthog.setPersonPropertiesForFlags({ emailDomain: domain, organizationId: org });
};

const subscribeSettled = (onChange: () => void) => {
  settledListeners.add(onChange);
  return () => settledListeners.delete(onChange);
};

/**
 * Reads a PostHog feature flag.
 *
 * Returns `undefined` while flags are still loading, then `true` / `false`. The user's email *domain*
 * (as `emailDomain`) and the id of the org they're currently on (as `organizationId`) are pushed via
 * `setPersonPropertiesForFlags` before evaluation — so domain- and org-targeted flags resolve while the
 * identifiable address never reaches PostHog. On an org switch the cached value is withheld (returns
 * `undefined`) until a reload *requested for the current org* lands, so a gate never flashes the wrong
 * org's content. Returns `true` on localhost when PostHog isn't configured (see FORCE_ON); otherwise it
 * follows the flag (and `undefined` when there's no provider at all).
 */
export const usePostHogFeatureFlag = (flag: string): boolean | undefined => {
  const posthog = usePostHog();
  const email = useCurrentUser((x) => x.value?.email);
  const currentOrg: OrgKey = useCurrentClient((x) => x.value?.id) ?? null;

  useEffect(() => {
    const domain = email ? getEmailDomain(email) : undefined;
    if (!domain || !posthog) return;
    bindCoordinator(posthog);
    requestFlagsForOrg(posthog, domain, currentOrg);
  }, [email, currentOrg, posthog]);

  // Re-render when the coordinator settles on a new org — `useFeatureFlagEnabled` alone wouldn't, since
  // the flag value can be unchanged across orgs while the gate still needs to open.
  const settled = useSyncExternalStore(subscribeSettled, () => settledOrg);

  const enabled = useFeatureFlagEnabled(flag);

  if (FORCE_ON) return true;
  // Withhold until the loaded flags were evaluated against the org we're currently on.
  if (settled !== currentOrg) return undefined;
  return enabled;
};
